import { useRef, useState } from 'react';
import { GripVertical, X, Plus, RotateCcw, Check, Pencil, ChevronLeft, ChevronRight } from 'lucide-react';
import { WIDGETS, widgetDef, type WidgetDef } from './widgets';
import type { DashboardWidget } from '../../api/dashboard';

interface Props {
  widgets: DashboardWidget[];
  editing: boolean;
  saving: boolean;
  onEdit: () => void;
  onChange: (next: DashboardWidget[]) => void;
  onSave: () => void;
  onCancel: () => void;
  onReset: () => void;
}

const COLS = 12;
const MIN_H = 120;
const MAX_H = 1200;

/** Named fractions of the 12-column grid, for the width dropdown. */
const WIDTH_CHOICES: { value: number; label: string }[] = [
  { value: 2, label: 'Sixth' },
  { value: 3, label: 'Quarter' },
  { value: 4, label: 'Third' },
  { value: 6, label: 'Half' },
  { value: 8, label: 'Two thirds' },
  { value: 9, label: 'Three quarters' },
  { value: 12, label: 'Full width' },
];

type Drag =
  | { kind: 'resize'; index: number; axis: 'x' | 'y' | 'xy'; startX: number; startY: number; startW: number; startH: number; colPx: number }
  | null;

/**
 * The Home dashboard: a 12-column grid where each widget spans 1–12 columns
 * and optionally pins its own height (scrolling its body when the content is
 * taller). Twelve columns so a stat tile can sit at a sixth of the row —
 * enough of them fit across to be worth the space they take.
 *
 * Resizing is drag handles on the right edge (width), bottom edge (height)
 * and corner (both), with dropdowns beside them: drag alone would strand
 * touch and keyboard users. Reordering likewise pairs drag-and-drop with
 * arrow buttons.
 */
export default function DashboardGrid({
  widgets, editing, saving, onEdit, onChange, onSave, onCancel, onReset,
}: Props) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);
  const [resize, setResize] = useState<Drag>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const move = (from: number, to: number) => {
    if (to < 0 || to >= widgets.length || from === to) return;
    const next = [...widgets];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  const update = (i: number, patch: Partial<DashboardWidget>) => {
    onChange(widgets.map((w, idx) => (idx === i ? { ...w, ...patch } : w)));
  };

  const remove = (i: number) => onChange(widgets.filter((_, idx) => idx !== i));

  const add = (def: WidgetDef) => {
    const config: Record<string, string> = {};
    def.options.forEach(o => { config[o.key] = o.default; });
    onChange([
      ...widgets,
      {
        // Date.now alone collides when two widgets are added in the same tick.
        id: `w-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
        type: def.type,
        w: def.defaultWidth,
        ...(def.defaultHeight ? { h: def.defaultHeight } : {}),
        config,
      },
    ]);
    setAdding(false);
  };

  // ── Resize ────────────────────────────────────────────────
  // Pointer events (not mouse) so this works with touch and pen, with capture
  // so the drag survives the pointer leaving the handle.
  const startResize = (
    e: React.PointerEvent,
    index: number,
    axis: 'x' | 'y' | 'xy',
    el: HTMLElement,
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const gridW = gridRef.current?.getBoundingClientRect().width ?? 1200;
    const gap = 16;
    const colPx = (gridW - gap * (COLS - 1)) / COLS + gap;
    const w = widgets[index];
    setResize({
      kind: 'resize', index, axis,
      startX: e.clientX,
      startY: e.clientY,
      startW: w.w || 4,
      startH: w.h ?? el.closest('.dash-item')?.getBoundingClientRect().height ?? 240,
      colPx,
    });
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const onResizeMove = (e: React.PointerEvent) => {
    if (!resize) return;
    const patch: Partial<DashboardWidget> = {};
    if (resize.axis === 'x' || resize.axis === 'xy') {
      const cols = Math.round((e.clientX - resize.startX) / resize.colPx);
      const def = widgetDef(widgets[resize.index].type);
      const min = def?.minWidth ?? 1;
      patch.w = Math.min(COLS, Math.max(min, resize.startW + cols));
    }
    if (resize.axis === 'y' || resize.axis === 'xy') {
      const next = resize.startH + (e.clientY - resize.startY);
      patch.h = Math.min(MAX_H, Math.max(MIN_H, Math.round(next)));
    }
    update(resize.index, patch);
  };

  const endResize = () => setResize(null);

  return (
    <div className="space-y-3">
      {/* Toolbar */}
      <div className="flex items-center gap-2 flex-wrap">
        {editing ? (
          <>
            <button type="button" className="btn btn-primary" onClick={onSave} disabled={saving}>
              <Check className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Done'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setAdding(v => !v)}>
              <Plus className="w-3.5 h-3.5" /> Add widget
            </button>
            <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={saving}>
              Cancel
            </button>
            <span className="text-[11px] text-muted hidden lg:inline">
              Drag a widget's right or bottom edge to resize it.
            </span>
            <button type="button" className="btn btn-ghost ml-auto text-muted" onClick={onReset} disabled={saving}>
              <RotateCcw className="w-3.5 h-3.5" /> Reset to default
            </button>
          </>
        ) : (
          <button type="button" className="btn btn-secondary ml-auto" onClick={onEdit}>
            <Pencil className="w-3.5 h-3.5" /> Customize
          </button>
        )}
      </div>

      {/* Widget picker */}
      {editing && adding && (
        <div className="card p-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
          {WIDGETS.map(def => (
            <button
              key={def.type}
              type="button"
              onClick={() => add(def)}
              className="text-left p-3 rounded-lg border border-line hover:border-primary hover:bg-row-odd transition-colors"
            >
              <p className="text-[13px] font-semibold text-fg">{def.name}</p>
              <p className="text-[11px] text-muted mt-0.5">{def.description}</p>
            </button>
          ))}
        </div>
      )}

      {widgets.length === 0 ? (
        <div className="card p-8 text-center text-muted text-sm">
          <p>Your dashboard is empty.</p>
          <p className="text-xs mt-1">
            {editing ? 'Use "Add widget" above to build it.' : 'Click Customize to add widgets.'}
          </p>
        </div>
      ) : (
        <div
          ref={gridRef}
          className="dash-grid"
          onPointerMove={resize ? onResizeMove : undefined}
          onPointerUp={resize ? endResize : undefined}
          onPointerCancel={resize ? endResize : undefined}
          // Dragging an edge otherwise selects the widget's text as it goes.
          style={resize ? { userSelect: 'none' } : undefined}
        >
          {widgets.map((w, i) => {
            const def = widgetDef(w.type);
            const span = Math.min(COLS, Math.max(1, w.w || 4));
            const resizingThis = resize?.index === i;

            return (
              <section
                key={w.id}
                className={`dash-item card flex flex-col relative ${
                  overIndex === i && dragIndex !== null && dragIndex !== i ? 'ring-2 ring-[color:var(--scpa-primary)]' : ''
                } ${dragIndex === i ? 'opacity-50' : ''}`}
                style={{
                  padding: 0,
                  ['--span' as any]: span,
                  // Tablets get roughly half as many across, never one.
                  ['--span-md' as any]: Math.min(COLS, span * 2),
                  ...(w.h ? { height: w.h } : {}),
                }}
                // Dragging to reorder must not fight dragging to resize. The
                // `draggable` flag alone isn't enough: pointerdown sets resize
                // state asynchronously, so dragstart can still fire first.
                draggable={editing && !resize}
                onDragStart={(e) => {
                  if (resize) { e.preventDefault(); return; }
                  setDragIndex(i);
                }}
                onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
                onDragOver={(e) => { if (editing && dragIndex !== null) { e.preventDefault(); setOverIndex(i); } }}
                onDrop={(e) => {
                  if (!editing || dragIndex === null) return;
                  e.preventDefault();
                  move(dragIndex, i);
                  setDragIndex(null);
                  setOverIndex(null);
                }}
              >
                {/* Header */}
                <div className="flex items-center gap-2 px-3 py-2 border-b border-line-light shrink-0">
                  {editing && <GripVertical className="w-3.5 h-3.5 text-muted shrink-0 cursor-grab" />}
                  <h2 className="text-[13px] font-semibold text-fg truncate">
                    {def ? def.titleOf(w.config ?? {}) : 'Unavailable widget'}
                  </h2>
                  {editing && (
                    <div className="ml-auto flex items-center gap-0.5 shrink-0">
                      <button type="button" className="p-1 text-muted hover:text-fg" title="Move earlier"
                        onClick={() => move(i, i - 1)} disabled={i === 0}>
                        <ChevronLeft className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" className="p-1 text-muted hover:text-fg" title="Move later"
                        onClick={() => move(i, i + 1)} disabled={i === widgets.length - 1}>
                        <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" className="p-1 text-muted hover:text-err" title="Remove"
                        onClick={() => remove(i)}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                {/* Config row */}
                {editing && def && (
                  <div className="px-3 py-2 border-b border-line-light bg-row-odd flex flex-wrap gap-2 shrink-0">
                    {def.options.map(opt => (
                      <label key={opt.key} className="flex items-center gap-1 text-[11px] text-muted">
                        {opt.label}
                        <select
                          className="select text-[11px] py-0.5"
                          value={String(w.config?.[opt.key] ?? opt.default)}
                          onChange={(e) => update(i, { config: { ...(w.config ?? {}), [opt.key]: e.target.value } })}
                        >
                          {opt.choices.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                      </label>
                    ))}
                    <label className="flex items-center gap-1 text-[11px] text-muted">
                      Width
                      <select
                        className="select text-[11px] py-0.5"
                        value={String(span)}
                        onChange={(e) => update(i, { w: Number(e.target.value) })}
                      >
                        {/* A dragged width may not equal a named fraction. */}
                        {!WIDTH_CHOICES.some(c => c.value === span) && (
                          <option value={span}>{span}/12</option>
                        )}
                        {WIDTH_CHOICES.filter(c => c.value >= (def.minWidth ?? 1)).map(c => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </label>
                    <label className="flex items-center gap-1 text-[11px] text-muted">
                      Height
                      <select
                        className="select text-[11px] py-0.5"
                        value={w.h ? String(w.h) : 'auto'}
                        onChange={(e) => update(i, {
                          h: e.target.value === 'auto' ? undefined : Number(e.target.value),
                        })}
                      >
                        <option value="auto">Fit content</option>
                        {![200, 320, 460, 640].includes(w.h ?? 0) && w.h && (
                          <option value={w.h}>{w.h}px</option>
                        )}
                        <option value="200">Short</option>
                        <option value="320">Medium</option>
                        <option value="460">Tall</option>
                        <option value="640">Very tall</option>
                      </select>
                    </label>
                  </div>
                )}

                {/* Body — scrolls when the widget has a pinned height, so a
                    10-row list fits in a short widget and the rest scrolls. */}
                <div className={`px-3 py-2.5 flex-1 min-w-0 ${w.h ? 'overflow-y-auto' : ''}`}>
                  {def ? (
                    <def.Component config={w.config ?? {}} />
                  ) : (
                    <p className="text-[12px] text-muted py-3 text-center">
                      This widget type is no longer available. Remove it while customizing.
                    </p>
                  )}
                </div>

                {/* Resize handles */}
                {editing && (
                  <>
                    <div
                      role="separator"
                      aria-label="Resize width"
                      className={`dash-resize dash-resize-x ${resizingThis && resize?.axis === 'x' ? 'is-active' : ''}`}
                      onPointerDown={(e) => startResize(e, i, 'x', e.currentTarget)}
                    />
                    <div
                      role="separator"
                      aria-label="Resize height"
                      className={`dash-resize dash-resize-y ${resizingThis && resize?.axis === 'y' ? 'is-active' : ''}`}
                      onPointerDown={(e) => startResize(e, i, 'y', e.currentTarget)}
                    />
                    <div
                      role="separator"
                      aria-label="Resize width and height"
                      className={`dash-resize dash-resize-xy ${resizingThis && resize?.axis === 'xy' ? 'is-active' : ''}`}
                      onPointerDown={(e) => startResize(e, i, 'xy', e.currentTarget)}
                    />
                  </>
                )}
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
